<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useConvexAuth } from '../composables/useConvexAuth';
import { useConvexClient } from 'convex-vue';
import { api } from '../../convex/_generated/api';

const route = useRoute();
const client = useConvexClient();

const email = ref('');
const submittedEmail = ref('');
const isLoading = ref(false);
const error = ref('');
const emailSent = ref(false);

const { signIn, storeRedirectUrl } = useConvexAuth();

// Get invite token from query params (for invite links)
const inviteToken = computed(() => route.query.invite as string | undefined);
const isInviteLink = computed(() => !!inviteToken.value);
const inviteRedirectPath = computed(() =>
  inviteToken.value ? `/invite/${encodeURIComponent(inviteToken.value)}` : '/workshops'
);

// Store redirect URL for after auth
if (inviteToken.value) {
  storeRedirectUrl(inviteRedirectPath.value);
}

// Random rotations for step cards
const stepRotations = [
  (Math.random() - 0.5) * 4,
  (Math.random() - 0.5) * 4,
  (Math.random() - 0.5) * 4,
];

const handleSendMagicLink = async () => {
  if (!email.value || isLoading.value) return;

  isLoading.value = true;
  error.value = '';
  submittedEmail.value = email.value;

  try {
    if (isInviteLink.value) {
      if (!inviteToken.value) {
        throw new Error('Invalid invite link');
      }
      await client.mutation(api.inviteLinks.createPendingInviteClaim, {
        email: email.value.toLowerCase(),
        token: inviteToken.value,
      });
    }

    // For non-invite login, check eligibility first (silently)
    // We check to prevent abuse, but don't reveal the result to prevent user enumeration
    let shouldSendEmail = true;
    if (!isInviteLink.value) {
      const eligibility = await client.query(api.auth.checkLoginEligibility, {
        email: email.value.toLowerCase(),
      });
      // Only send if user can login (known user or allowed creator)
      shouldSendEmail = eligibility.canLogin;
    }

    // Only actually send the magic link if eligible
    // This prevents spam/abuse while maintaining security through obscurity
    if (shouldSendEmail) {
      await signIn('resend', {
        email: email.value,
        redirectTo: inviteRedirectPath.value,
      });
    }

    // Always show success to prevent user enumeration attacks
    // Whether the email was actually sent or not is not revealed
    emailSent.value = true;
  } catch (e: any) {
    // Only show errors for technical failures, not policy decisions
    const message = e.message || 'Failed to send magic link';
    error.value = message;
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div
    class="cork-board min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden"
  >
    <!-- Decorative scattered post-its -->
    <div class="scattered-postits"></div>

    <!-- Additional decorative elements -->
    <div
      class="absolute top-16 left-8 w-20 h-20 bg-(--color-postit-yellow) opacity-10 -rotate-12 shadow-lg pointer-events-none"
    ></div>
    <div
      class="absolute bottom-24 right-12 w-24 h-24 bg-(--color-postit-pink) opacity-10 rotate-15 shadow-lg pointer-events-none"
    ></div>
    <div
      class="absolute top-1/4 right-8 w-16 h-16 bg-(--color-postit-blue) opacity-10 rotate-25 shadow-lg pointer-events-none"
    ></div>

    <div class="max-w-lg w-full relative z-10">
      <!-- Invite Link Header with pinned card -->
      <template v-if="isInviteLink">
        <div class="text-center mb-6">
          <div
            class="paper-card cork-pin p-6 rounded-sm inline-block mb-6"
            :style="{ '--card-rotation': '-1deg' }"
          >
            <div class="paper-texture rounded-sm"></div>
            <div class="relative z-10">
              <div
                class="w-16 h-16 mx-auto mb-4 rounded-full bg-(--color-postit-green) flex items-center justify-center shadow-md"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-8 w-8 text-gray-700"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h1 class="text-3xl font-bold text-gray-800 mb-2">{{ $t('login.invited') }}</h1>
              <p class="text-gray-600">
                {{ $t('login.invitedDescription') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Steps as numbered sticky notes -->
        <div class="grid grid-cols-3 gap-3 mb-8">
          <div
            v-for="(step, index) in [
              { num: '1', text: $t('login.step1') },
              { num: '2', text: $t('login.step2') },
              { num: '3', text: $t('login.step3') },
            ]"
            :key="index"
            class="paper-card p-3 rounded-sm text-center"
            :style="{ '--card-rotation': stepRotations[index] + 'deg' }"
          >
            <div class="paper-texture rounded-sm"></div>
            <div class="relative z-10">
              <div
                class="w-8 h-8 mx-auto mb-2 rounded-full bg-(--color-primary) text-white flex items-center justify-center text-sm font-bold shadow-md"
              >
                {{ step.num }}
              </div>
              <p class="text-gray-700 text-sm font-medium">{{ step.text }}</p>
            </div>
          </div>
        </div>
      </template>

      <!-- Regular Login Header -->
      <template v-else>
        <div class="text-center mb-8">
          <div
            class="paper-card cork-pin p-6 rounded-sm inline-block"
            :style="{ '--card-rotation': '0.5deg' }"
          >
            <div class="paper-texture rounded-sm"></div>
            <div class="relative z-10">
              <h1 class="text-4xl font-bold text-gray-800 mb-2">{{ $t('login.welcomeBack') }}</h1>
              <p class="text-gray-600">{{ $t('login.signInToAccess') }}</p>
            </div>
          </div>
        </div>
      </template>

      <!-- Success State - Green Post-it -->
      <div
        v-if="emailSent"
        class="paper-card postit-green p-6 rounded-sm text-center"
        :style="{ '--card-rotation': (Math.random() - 0.5) * 2 + 'deg' }"
      >
        <div class="paper-texture rounded-sm"></div>
        <div class="relative z-10">
          <div
            class="w-14 h-14 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-7 w-7 text-green-700"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h3 class="text-xl font-bold text-gray-800 mb-2">{{ $t('auth.checkEmail') }}</h3>
          <p class="text-green-700 font-medium mb-1">
            {{
              isInviteLink
                ? $t('login.magicLinkSentInvite', { email: submittedEmail })
                : $t('login.magicLinkSent', { email: submittedEmail })
            }}
          </p>
          <p class="text-gray-500 text-sm">{{ $t('login.checkSpam') }}</p>
        </div>
      </div>

      <!-- Login Form - Pinned paper card -->
      <div
        v-else
        class="paper-card cork-pin p-6 md:p-8 rounded-sm"
        :style="{ '--card-rotation': (Math.random() - 0.5) * 2 + 'deg' }"
      >
        <div class="paper-texture rounded-sm"></div>

        <form @submit.prevent="handleSendMagicLink" class="relative z-10 space-y-5">
          <div>
            <label class="block text-gray-700 font-medium mb-2">{{ $t('auth.email') }}</label>
            <input
              v-model="email"
              type="email"
              :placeholder="$t('login.emailPlaceholder')"
              class="handwritten-input w-full text-gray-800"
              required
            />
          </div>

          <div v-if="error" class="p-3 bg-red-100 border border-red-200 rounded-lg">
            <p class="text-red-700 text-sm">{{ error }}</p>
          </div>

          <button
            type="submit"
            :disabled="isLoading"
            class="push-button w-full px-6 py-4 text-lg rounded-lg"
          >
            {{
              isLoading
                ? $t('common.loading')
                : isInviteLink
                  ? $t('auth.sendMagicLink')
                  : $t('auth.sendMagicLink')
            }}
          </button>

          <p v-if="!isInviteLink" class="text-gray-500 text-sm text-center">
            {{ $t('login.forReturningUsers') }}
          </p>
        </form>
      </div>
    </div>
  </div>
</template>
